// The live link to a running game, shared by every tab: connect once (port + token, remembered in this browser), then
// the Live tab, Tuning and the characters' "in game" buttons use the same client.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { DEFAULT_PORT, LiveLinkClient, type Hello } from "./client";

const KEY = "delve-workshop:livelink";

export interface LinkSettings { port: number; token: string }

export interface LiveLinkState {
  client: LiveLinkClient;
  settings: LinkSettings;
  status: "idle" | "connecting" | "connected" | "error";
  hello: Hello | null;
  error: string | null;
  connect(settings: LinkSettings): Promise<void>;
  disconnect(): void;
}

function remembered(): LinkSettings {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "{}") as Partial<LinkSettings>;
    return { port: Number(raw.port) || DEFAULT_PORT, token: typeof raw.token === "string" ? raw.token : "" };
  } catch {
    return { port: DEFAULT_PORT, token: "" };
  }
}

const LiveLinkContext = createContext<LiveLinkState | null>(null);

export function LiveLinkProvider({ children, client: given }: { children: ReactNode; client?: LiveLinkClient }) {
  const client = useMemo(() => given ?? new LiveLinkClient(), [given]);
  const [settings, setSettings] = useState(remembered);
  const [status, setStatus] = useState<LiveLinkState["status"]>("idle");
  const [hello, setHello] = useState<Hello | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const off = client.onClosed(() => {
      setStatus("idle");
      setHello(null);
    });
    return () => {
      off();
      client.close();
    };
  }, [client]);

  const connect = useCallback(async (next: LinkSettings) => {
    setSettings(next);
    try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* storage blocked */ }
    setStatus("connecting");
    setError(null);
    try {
      setHello(await client.connect(next.port, next.token));
      setStatus("connected");
    } catch (e) {
      setHello(null);
      setStatus("error");
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  const disconnect = useCallback(() => {
    client.close();
    setStatus("idle");
    setHello(null);
  }, [client]);

  const value = useMemo<LiveLinkState>(
    () => ({ client, settings, status, hello, error, connect, disconnect }),
    [client, settings, status, hello, error, connect, disconnect],
  );
  return <LiveLinkContext.Provider value={value}>{children}</LiveLinkContext.Provider>;
}

export function useLiveLink(): LiveLinkState {
  const value = useContext(LiveLinkContext);
  if (!value) throw new Error("useLiveLink outside <LiveLinkProvider>");
  return value;
}

/** The link when the page sits under a <LiveLinkProvider>, else null (views that also work with no game: tests,
 * read-only pages). */
export function useOptionalLiveLink(): LiveLinkState | null {
  return useContext(LiveLinkContext);
}
